(ns common.db.table.backend
  (:require [clojure.string :as str]))

(defprotocol TableBackend
  (on-init [this page search sorting daterange]
    "Return an event vector to be dispatched when the table is initialized.")
  (on-change [this page search sorting daterange]
    "Return an event vector to be dispatched when table state changes.")
  (confirm-search [this page search sorting]
    "Return an event vector to be dispatched when search is confirmed.")
  (get-page-count [this page filtered-value]
    "Return the number of pages in `filtered-value`.")
  (apply-sorting [this column direction value]
    "Sort `value` on `column` in ascending or descending `direction`.")
  (apply-filter [this column term value]
    "Filter `value` by comparing the value at `column` with `term`.")
  (apply-pagination [this cursor size value]
    "Return subset of `value` based on page `cursor` and `size`.")
  (apply-hidden [this column hidden? value]
    "Hides/shows subset of `value` where `column` is `false`")
  (apply-daterange [this page search sorting value]
    "Return statistics based on `value`."))

(defrecord RemoteBackend [on-init on-change]
  TableBackend
  (on-init [_ page search sorting daterange]
    (when on-init
      (into on-init [(* (:size page) (:cursor page)) search sorting daterange])))
  (on-change [_ page search sorting daterange]
    (when on-change
      (into on-change [(* (:size page) (:cursor page)) search sorting daterange])))
  (confirm-search [_ page search sorting]
    (into on-change [(* (:size page) (:cursor page)) search sorting]))
  (get-page-count [_ page filtered-value]
    (Math/ceil (/ (get-in filtered-value [:meta :total]) (:size page))))
  (apply-pagination [_ _cursor _size value] value)
  (apply-sorting [_ _column _direction value] value)
  (apply-filter [_ _column _term value] value)
  (apply-hidden [_ _column _hidden? value] value)
  (apply-daterange [_ _page _search _sorting value] value))

(defrecord RemoteScrollingBackend [on-init on-change]
  TableBackend
  (on-init [_ page _search _sorting _daterange]
    (when on-init
      (into on-init [page])))
  (on-change [_ page _ _ _]
    (when on-change
      (into on-change [page])))
  (confirm-search [_ _page _search _sorting])
  (get-page-count [_ _page _filtered-value])
  (apply-pagination [_ _cursor _size value] value)
  (apply-sorting [_ _column _direction value] value)
  (apply-filter [_ _column _term value] value)
  (apply-hidden [_ _column _hidden? value] value)
  (apply-daterange [_ _page _search _sorting value] value))

(defrecord InMemoryBackend [on-init on-change]
  TableBackend
  (on-init [_ _ _ _ daterange]
    (when on-init
      (into on-init [daterange])))
  (on-change [_ _page _search _sorting _daterange] nil)
  (confirm-search [_ _page _search _sorting] nil)
  (apply-daterange [_ _page _search _sorting value]
    (when on-change
      (into on-change [value])))
  (get-page-count [_ page filtered-value]
    (Math/ceil (/ (count (:value filtered-value)) (:size page))))

  (apply-sorting [_ column direction value]
    (let [f (cond
              (nil? column) identity
              (= :asc direction) (partial sort-by column)
              (= :desc direction) (comp reverse (partial sort-by column))
              :else identity)]
      (update value :value f)))

  (apply-filter [_ column term value]
    (letfn [(match? [m]
              (str/includes? (str/lower-case (column m))
                             (str/lower-case term)))]
      (if (and column term)
        (update value :value (partial filter match?))
        value)))

  (apply-hidden [_ column hidden? value]
    (if hidden?
      (update value :value (partial remove column))
      value))

  (apply-pagination [_ cursor size value]
    (letfn [(f [coll]
              (if (> (count coll) (* size cursor))
                (nth (partition-all size coll) cursor)
                (list)))]
      (if (and cursor size)
        (update value :value f)
        value))))
